import { toCamelCase } from '~/utils/object'
import { inject } from 'tsyringe'
import { containerScoped } from '~/decorators/dependency-container'
import RequestBuilder from '~/builders/http/RequestBuilder'
import {
  ManagerGeneralSettingsGetResponse,
  ManagerGeneralSettingsPostResponse,
  ManagerSaleRequests,
  ManagerSaleRequest,
  GuestSaleRequests,
  SaleRequest,
  CreateSaleRequestItemSchemaAndFields,
  SubmitNewSaleRequestItemResponse,
  ManagerSaleRequestItemsList,
  ManagerSaleRequestItem,
  GetManagersWillingToBuyClassifiedResponse,
  ReportSaleRequestResponse,
  ManagerSaleRequestItemResponse,
  DuplicateSaleRequestItemSchemaAndFields
} from '~/models/sale-requests'
import { toSnakeCase } from '~/utils/snake-case'

@containerScoped()
export default class SaleRequestsService {
  constructor(@inject(RequestBuilder) private requestBuilder: RequestBuilder) {}

  public getManagerGeneralSettings(): Promise<
    ManagerGeneralSettingsGetResponse
  > {
    return this.requestBuilder
      .request('GET', '/api/sale-requests/managers/general-settings')
      .send()
  }

  public getManagerSaleRequests(pg?: number): Promise<ManagerSaleRequests> {
    return this.requestBuilder
      .request('GET', '/api/sale-requests/managers/requests')
      .params({ pg })
      .send()
  }

  public getGuestSaleRequests(pg?: number): Promise<GuestSaleRequests> {
    return this.requestBuilder
      .request('GET', '/api/sale-requests/requests')
      .params({ pg })
      .send()
  }

  public setManagerGeneralSettings(payload: {
    disabled: boolean
    distance: number
    disabledFrom?: string
    disabledTo?: string
  }): Promise<ManagerGeneralSettingsPostResponse> {
    return this.requestBuilder
      .request('POST', '/api/sale-requests/managers/general-settings')
      .data(toSnakeCase(payload))
      .validate(body => body)
      .map(body => toCamelCase(body))
      .send()
  }

  public updateManagerSaleRequestStatus(
    id: number,
    status: string
  ): Promise<ManagerSaleRequest> {
    return this.requestBuilder
      .request('PUT', `/api/sale-requests/managers/requests/${id}/status`)
      .data({ status })
      .validate(body => body)
      .map(body => toCamelCase(body))
      .send()
  }

  cancelSaleRequest(id: number): Promise<ManagerSaleRequest> {
    return this.requestBuilder
      .request('DELETE', `/api/sale-requests/requests/${id}/cancel/`)
      .send()
  }

  public getManagersSaleRequestForm(
    form?: any
  ): Promise<CreateSaleRequestItemSchemaAndFields> {
    return this.requestBuilder
      .request('GET', '/api/sale-requests/managers/items/new')
      .params(toSnakeCase(form))
      .send()
  }

  public createNewSaleRequest(
    payload: any
  ): Promise<SubmitNewSaleRequestItemResponse> {
    return this.requestBuilder
      .request('POST', '/api/sale-requests/managers/items/new')
      .data(toSnakeCase(payload))
      .validate(body => body)
      .map(body => toCamelCase(body))
      .send()
  }

  public getManagerSaleRequestItemsList(
    payload?: any
  ): Promise<ManagerSaleRequestItemsList> {
    return this.requestBuilder
      .request('GET', '/api/sale-requests/managers/items')
      .params(toSnakeCase(payload))
      .send()
  }

  public toggleSaleRequestItemStatus(
    enable: boolean,
    id: number
  ): Promise<ManagerSaleRequestItem> {
    return this.requestBuilder
      .request(
        enable ? 'POST' : 'DELETE',
        `/api/sale-requests/managers/items/${id}/activation`
      )
      .send()
  }

  public deleteSaleRequestItem(id: number): Promise<any> {
    return this.requestBuilder
      .request('DELETE', `/api/sale-requests/managers/items/${id}`)
      .send()
  }

  public deleteSaleRequestItems(ids: number[]): Promise<any> {
    return this.requestBuilder
      .request('DELETE', `/api/sale-requests/managers/items`)
      .params({ items_ids: ids })
      .send()
  }

  public getDuplicatedFieldsFromSaleRequestItem(
    id: number | string
  ): Promise<DuplicateSaleRequestItemSchemaAndFields> {
    return this.requestBuilder
      .request('GET', `/api/sale-requests/managers/items/${id}/duplicate`)
      .send()
  }

  public getSaleRequestItem(
    id: number | string,
    payload?: any
  ): Promise<ManagerSaleRequestItemResponse> {
    return this.requestBuilder
      .request('GET', `/api/sale-requests/managers/items/${id}`)
      .params(toSnakeCase(payload))
      .send()
  }

  public editSaleRequestItem(
    id: number | string,
    payload: any
  ): Promise<SubmitNewSaleRequestItemResponse> {
    return this.requestBuilder
      .request('PUT', `/api/sale-requests/managers/items/${id}`)
      .data(toSnakeCase(payload))
      .validate(body => body)
      .map(body => toCamelCase(body))
      .send()
  }

  public updateSaleRequestItem(
    id: number
  ): Promise<SubmitNewSaleRequestItemResponse> {
    return this.requestBuilder
      .request('POST', `/api/sale-requests/managers/items/${id}/update`)
      .send()
  }

  public getManagersWillingToBuyClassified(
    id: string
  ): Promise<GetManagersWillingToBuyClassifiedResponse> {
    return this.requestBuilder
      .request('GET', `/api/sale-requests/${id}/fetch`)
      .send()
  }

  public createSaleRequestToManager({
    classifiedId,
    itemId,
    dealerId
  }: {
    classifiedId: string
    itemId: number
    dealerId: number
  }): Promise<SaleRequest> {
    return this.requestBuilder
      .request('POST', `/api/sale-requests/${classifiedId}/submit`)
      .data({ sale_request_item_id: itemId, dealer_id: dealerId })
      .validate(body => body)
      .map(body => toCamelCase(body))
      .send()
  }

  public async getReportSaleRequestSchema(
    reporter: 'user' | 'manager',
    id: string
  ): Promise<ReportSaleRequestResponse> {
    const url =
      reporter === 'user'
        ? `/api/sale-requests/requests/${id}/report-manager`
        : `/api/sale-requests/managers/requests/${id}/report`

    return await this.requestBuilder.request('GET', url).send()
  }

  public async reportSaleRequest(
    reporter: string,
    id: string,
    reason: number,
    details: string
  ): Promise<any> {
    let endpoint = ''

    if (reporter === 'user') {
      endpoint = `/api/sale-requests/requests/${id}/report-manager`
    } else if (reporter === 'manager') {
      endpoint = `/api/sale-requests/managers/requests/${id}/report`
    }

    return await this.requestBuilder
      .request('POST', endpoint)
      .data({ reason, details })
      .validate(body => body)
      .map(body => toCamelCase(body))
      .send()
  }
}
